import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, Zap, CheckCircle, ArrowRight, Waves, Gauge, Filter, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import LazyImage from '../components/LazyImage';

const PipelessFiltration = () => {
  const navigate = useNavigate();
  const [hoveredProduct, setHoveredProduct] = useState(null);

  const comparisonData = [
    { feature: 'Chamber', fx181: 'Single', nfx201: 'Dual' },
    { feature: 'Pump Power', fx181: '1hp Single phase', nfx201: '1.5hp Single phase' },
    { feature: 'Voltage', fx181: '220-240V', nfx201: '220-240V' },
    { feature: 'Flow Rate', fx181: '20,000 L/hr', nfx201: '30,000 L/hr' },
    { feature: 'Filtration', fx181: '5/15-micron membrane', nfx201: '5/15-micron membrane' },
    { feature: 'Pool Capacity', fx181: 'Up to 75,000 L', nfx201: 'Up to 1,10,000 L' },
    { feature: 'Installation', fx181: 'Wall-hanging', nfx201: 'Deck-integrated' },
    { feature: 'Best For', fx181: 'Concrete/Tile/Liner pools', nfx201: 'Concrete/Tile/Liner pools' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cream-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1755547721795-8af6746abffd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwb29sJTIwZmlsdHJhdGlvbiUyMHN5c3RlbSUyMHRlY2hub2xvZ3klMjBibHVlJTIwd2F0ZXJ8ZW58MHx8fHwxNzczNjgyMDA3fDA&ixlib=rb-4.1.0&q=85"
            alt="Crystal clear pool water"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 via-aqua-900/80 to-navy-900/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-aqua-500/20 backdrop-blur-sm border border-aqua-400/30 rounded-full px-6 py-3 mb-8">
              <Zap className="w-5 h-5 text-aqua-400 animate-pulse" />
              <span className="text-aqua-300 font-semibold">Revolutionary Technology</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Revolutionary Pipeless
              <span className="block text-aqua-400 mt-2">Filtration Systems</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-4 text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Clean, Compact, Efficient
            </p>
            
            <p className="text-lg md:text-xl mb-10 text-aqua-200 max-w-3xl mx-auto">
              Transform your pool with wall-mounted or deck-integrated filters that eliminate messy pump rooms and traditional sand filters. Ideal for renovations and new installs up to 75,000 liters.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/booking')}
                className="bg-aqua-500 hover:bg-aqua-600 text-white px-10 py-7 text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                Get Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                onClick={() => window.location.href = 'tel:+917757999805'}
                className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/30 px-10 py-7 text-lg font-semibold transition-all duration-300"
              >
                Call Expert
              </Button>
            </div>
          </div>
        </div>

        {/* Animated water effect */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10"></div>
      </section>

      {/* Product Description Overview */}
      <section data-testid="product-description-overview" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8fbfd]">
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00AEEF] to-transparent"></div>
            <div className="py-10 px-6 md:px-12 text-center" style={{ fontSize: '18px', lineHeight: '1.6' }}>
              <p className="text-[#333]">
                <strong className="text-[#003366]">FX-181 Series</strong>, a Wall-Hanging Pipeless Membrane Filter System with flow rate{' '}
                <span className="text-[#00AEEF] font-semibold">20,000 litres per hour</span>, <span className="text-green-600 font-semibold">single-phase 220-240V for homes</span>, featuring an inbuilt filter and pump system. FX-181 series filters are wall-hanging filters, ready to install in existing pool structures. With its futuristic design and engineering excellence, the FX-181 series filters are fitted with 5/15-micron German membranes and LED lighting and are an ideal option for pool renovation work and in acute MEP failure situations. <span className="text-green-600 font-semibold">Perfect for Concrete/Tile/Liner pools.</span>{' '}
                <strong className="text-[#00AEEF]">NFX-201</strong>{' '}
                filters are designed for concrete and tile pools &amp; liner pools. The Monocoque FRP body separating the pump room and filter chamber is designed to be fitted on the periphery of the pool and integrated on the deck space. This filter is a complete replacement of traditional sand filters, eliminating messy pump rooms. Available in a deck-integrated dual filter chambers{' '}
                <strong className="text-[#00AEEF]">using a 1.5 HP pump with a single flow rate 30,000 litres per hour</strong>, <span className="text-green-600 font-semibold">single-phase 220-240V</span>. Designed for{' '}
                <span className="text-green-600 font-semibold">Concrete/Tile/Liner pools</span> up to{' '}
                <span className="text-[#00AEEF] font-semibold">1,10,000 litres</span>.
              </p>
            </div>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#00AEEF] to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Product 1: FX-181 Series */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Card 
            className="overflow-hidden border-0 shadow-2xl hover:shadow-aqua-500/20 transition-all duration-500"
            onMouseEnter={() => setHoveredProduct('fx181')}
            onMouseLeave={() => setHoveredProduct(null)}
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image Section */}
              <div className="relative bg-gradient-to-br from-aqua-500 to-aqua-700 p-12 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:30px_30px]"></div>
                </div>
                
                <div className={`relative transform transition-transform duration-700 ${hoveredProduct === 'fx181' ? 'scale-105' : 'scale-100'}`}>
                  {/* Main Product Image */}
                  <div className="grid grid-cols-2 gap-4">
                    <LazyImage 
                      src="/assets/pipeless/fx181-1.jpeg"
                      alt="FX-181 Pipeless Filter installed in pool"
                      eager
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/fx181-2.jpeg"
                      alt="FX-181 Pool with filtration - Day view"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/fx181-3.jpeg"
                      alt="FX-181 Pool with filtration - Side view"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/fx181-4.jpeg"
                      alt="FX-181 Pool with filtration - Night view"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                  </div>
                  
                  {/* Subtle glow — no animation */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-aqua-400/20 rounded-full blur-3xl"></div>
                </div>
                
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
                  <span className="text-white font-bold text-sm">LED Equipped</span>
                </div>
              </div>

              {/* Content Section */}
              <CardContent className="p-12 flex flex-col justify-center bg-white">
                <div className="inline-flex items-center gap-2 bg-aqua-100 text-aqua-700 rounded-full px-4 py-2 mb-6 w-fit">
                  <Filter className="w-4 h-4" />
                  <span className="font-semibold text-sm">Wall-Hanging System</span>
                </div>

                <h2 className="text-4xl font-bold text-navy-900 mb-4">
                  FX-181 Series
                </h2>
                <p className="text-xl text-gray-600 mb-8">
                  Wall-Hanging Pipeless Membrane Filter System
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-aqua-50 to-transparent rounded-lg hover:from-aqua-100 transition-colors">
                    <div className="w-12 h-12 bg-aqua-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Gauge className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">Flow Rate: <span className="text-[#00AEEF]">20,000 litres per hour</span></p>
                      <p className="text-sm text-gray-600"><span className="text-green-600 font-medium">Single-phase 220-240V for homes</span> &bull; Up to 75,000 L pool capacity</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-aqua-50 to-transparent rounded-lg hover:from-aqua-100 transition-colors">
                    <div className="w-12 h-12 bg-aqua-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Settings className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">Single Chamber filter</p>
                      <p className="text-sm text-gray-600">Inbuilt 1hp Pump Single phase, Wall-hanging design</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-aqua-50 to-transparent rounded-lg hover:from-aqua-100 transition-colors">
                    <div className="w-12 h-12 bg-aqua-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Filter className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">5/15-micron membrane bag filtration</p>
                      <p className="text-sm text-gray-600">German membrane technology with LED lighting</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-aqua-50 to-transparent rounded-lg hover:from-aqua-100 transition-colors">
                    <div className="w-12 h-12 bg-aqua-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1"><span className="text-green-600">Perfect for Concrete/Tile/Liner pools</span></p>
                      <p className="text-sm text-gray-600">Ready to install in existing pool structures &bull; Ideal for renovations</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg"
                    onClick={() => navigate('/booking')}
                    className="bg-aqua-500 hover:bg-aqua-600 text-white px-8 py-6 text-lg font-semibold flex-1 md:flex-initial"
                  >
                    Get Quote
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-aqua-500 text-aqua-600 hover:bg-aqua-50 px-8 py-6 text-lg font-semibold flex-1 md:flex-initial"
                  >
                    Learn More
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-green-50 border-l-4 border-green-500 rounded">
                  <p className="text-green-800 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Ideal for Quick Retrofits
                  </p>
                </div>
              </CardContent>
            </div>
          </Card>
        </div>
      </section>

      {/* Product 2: NFX Series */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-navy-50">
        <div className="max-w-7xl mx-auto">
          <Card 
            className="overflow-hidden border-0 shadow-2xl hover:shadow-aqua-500/20 transition-all duration-500"
            onMouseEnter={() => setHoveredProduct('nfx')}
            onMouseLeave={() => setHoveredProduct(null)}
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Content Section - Left on Desktop */}
              <CardContent className="p-12 flex flex-col justify-center bg-white order-2 md:order-1">
                <div className="inline-flex items-center gap-2 bg-navy-100 text-navy-700 rounded-full px-4 py-2 mb-6 w-fit">
                  <Droplet className="w-4 h-4" />
                  <span className="font-semibold text-sm">Deck-Integrated System</span>
                </div>

                <h2 className="text-4xl font-bold text-navy-900 mb-2">
                  NFX-201 Filter
                </h2>
                <p className="text-sm font-semibold text-[#00AEEF] mb-2 tracking-wide">NFX — Nanofiltration Membranes</p>
                <p className="text-base text-gray-500 italic mb-8">A scientific reference to Synder filtration membranes with specific rejections.</p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-navy-50 to-transparent rounded-lg hover:from-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Gauge className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">Flow Rate: <span className="text-[#00AEEF]">30,000 litres per hour</span></p>
                      <p className="text-sm text-gray-600"><span className="text-[#00AEEF] font-medium">1.5 HP pump</span> &bull; <span className="text-green-600 font-medium">Single-phase 220-240V</span></p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-navy-50 to-transparent rounded-lg hover:from-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Settings className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">Dual Filter Chamber</p>
                      <p className="text-sm text-gray-600">Monocoque FRP body &bull; Deck-integrated design on pool periphery</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-navy-50 to-transparent rounded-lg hover:from-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Filter className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">5/15-micron membrane bag filtration</p>
                      <p className="text-sm text-gray-600">Complete replacement for traditional sand filters &bull; No messy pump rooms</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-navy-50 to-transparent rounded-lg hover:from-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Waves className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1">Pool Capacity: <span className="text-[#00AEEF]">Up to 1,10,000 litres</span></p>
                      <p className="text-sm text-gray-600">Integrated into the deck space for a clean, modern look</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-navy-50 to-transparent rounded-lg hover:from-navy-100 transition-colors">
                    <div className="w-12 h-12 bg-navy-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-navy-900 mb-1"><span className="text-green-600">Perfect for Concrete/Tile/Liner pools</span></p>
                      <p className="text-sm text-gray-600">Ideal for new constructions &bull; Eliminates messy pump rooms</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg"
                    onClick={() => navigate('/booking')}
                    className="bg-navy-900 hover:bg-navy-800 text-white px-8 py-6 text-lg font-semibold flex-1 md:flex-initial"
                  >
                    Get Quote
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-navy-900 text-navy-900 hover:bg-navy-50 px-8 py-6 text-lg font-semibold flex-1 md:flex-initial"
                  >
                    Learn More
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-blue-800 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Dual Chamber — Up to 1,10,000 Litres Capacity
                  </p>
                </div>
              </CardContent>

              {/* Image Section - Right on Desktop */}
              <div className="relative bg-gradient-to-br from-navy-900 to-navy-700 p-12 flex items-center justify-center overflow-hidden order-1 md:order-2">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:30px_30px]"></div>
                </div>
                
                <div className={`relative transform transition-transform duration-700 ${hoveredProduct === 'nfx' ? 'scale-105' : 'scale-100'}`}>
                  {/* Real NFX Product Images Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <LazyImage 
                      src="/assets/pipeless/nfx201-1.jpeg"
                      alt="NFX Deck-Integrated Filter - Installation"
                      eager
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/nfx201-2.jpeg"
                      alt="NFX Deck-Integrated Filter - Close-up"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/nfx201-3.jpeg"
                      alt="NFX Deck-Integrated Filter - Pool View"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                    <LazyImage 
                      src="/assets/pipeless/nfx201-4.jpeg"
                      alt="NFX Deck-Integrated Filter - Deck Integration"
                      className="w-full h-64 md:h-72 rounded-lg shadow-2xl"
                    />
                  </div>
                  
                  {/* Subtle glow — no animation */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-aqua-400/15 rounded-full blur-3xl"></div>
                </div>
                
                <div className="absolute top-6 left-6 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
                  <span className="text-white font-bold text-sm">Dual Filter Chamber</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-navy-900 mb-4">Compare Systems</h2>
            <p className="text-xl text-gray-600">Choose the perfect filtration solution for your pool</p>
          </div>

          <Card className="overflow-hidden shadow-2xl border-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-aqua-500 to-aqua-600">
                    <TableHead className="text-white font-bold text-lg">Feature</TableHead>
                    <TableHead className="text-white font-bold text-lg">FX-181</TableHead>
                    <TableHead className="text-white font-bold text-lg">NFX-201</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-aqua-50 transition-colors duration-200`}>
                      <TableCell className="font-semibold text-navy-900">{row.feature}</TableCell>
                      <TableCell>{row.fx181}</TableCell>
                      <TableCell>{row.nfx201}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </section>

      {/* Testimonial & CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Testimonial */}
            <div className="relative">
              <div className="absolute top-0 left-0 text-9xl text-aqua-400/20 font-serif leading-none">"</div>
              <blockquote className="relative z-10 pl-12 pt-8">
                <p className="text-2xl md:text-3xl font-light mb-6 leading-relaxed">
                  Saved us space and maintenance! The pipeless system transformed our apartment complex pool completely.
                </p>
                <footer className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-aqua-500 rounded-full flex items-center justify-center text-2xl font-bold">
                    BA
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Bangalore Apartment RWA</p>
                    <p className="text-aqua-300">Residential Complex</p>
                  </div>
                </footer>
              </blockquote>
            </div>

            {/* Contact Form Snippet */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardContent className="p-8">
                <h3 className="text-3xl font-bold mb-6">Get Your Free Consultation</h3>
                <p className="text-aqua-200 mb-6">Ready to upgrade your pool filtration? Our experts are here to help.</p>
                
                <div className="space-y-4">
                  <Button 
                    size="lg"
                    onClick={() => navigate('/booking')}
                    className="w-full bg-aqua-500 hover:bg-aqua-600 text-white py-6 text-lg font-semibold"
                  >
                    Request Detailed Quote
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => window.location.href = 'tel:+917757999805'}
                    variant="outline"
                    className="w-full border-2 border-white/50 text-white hover:bg-white/10 py-6 text-lg font-semibold"
                  >
                    Call 077579 99805
                  </Button>
                  <Button 
                    size="lg"
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="w-full border-2 border-white/50 text-white hover:bg-white/10 py-6 text-lg font-semibold"
                  >
                    Contact Us
                  </Button>
                </div>

                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-sm text-aqua-200 text-center">
                    ✓ Free site evaluation &nbsp; ✓ Expert consultation &nbsp; ✓ Competitive pricing
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PipelessFiltration;
